import { Link, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { api, ApiError } from '../lib/api';
import { WorkshopForm } from '../components/WorkshopForm';
import { useToast } from '../components/Toast';

export function AdminNewPage() {
  const navigate = useNavigate();
  const toast = useToast();

  const mutation = useMutation({
    mutationFn: (values: Parameters<typeof api.admin.createWorkshop>[0]) =>
      api.admin.createWorkshop(values),
    onSuccess: (r) => {
      if (r.workshop.zoomJoinUrl) {
        toast.success(
          'Draft saved · Zoom meeting created',
          'Refine the details, then click Publish to generate the cover image and go live.',
        );
      } else {
        toast.error(
          'Draft saved, but Zoom failed',
          'Click Retry on the Zoom panel of the edit page to provision the meeting.',
        );
      }
      navigate(`/admin/workshops/${r.workshop.id}`);
    },
    onError: (err) =>
      toast.error('Could not create draft', err instanceof ApiError ? err.message : String(err)),
  });

  return (
    <div className="container-page max-w-2xl py-12">
      <Link to="/admin" className="text-sm text-ink-muted hover:text-ink">← Back to admin</Link>
      <div className="mt-4 flex items-center gap-3">
        <span
          className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide"
          style={{ background: '#F0F0EC', color: '#525252' }}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-ink-subtle" />
          Draft
        </span>
        <h1 className="text-2xl font-semibold tracking-tight">New workshop</h1>
      </div>
      <p className="mt-1.5 text-sm text-ink-muted">
        Creates a draft and provisions the Zoom meeting immediately so you can verify it worked.
        The AI cover image is generated when you click <strong>Publish</strong>.
      </p>

      <div className="mt-8">
        <WorkshopForm
          onSubmit={(values) => mutation.mutate(values)}
          submitting={mutation.isPending}
          submitLabel="Save draft"
          cancelHref="/admin"
          error={mutation.isError && mutation.error instanceof ApiError ? mutation.error.message : null}
        />
      </div>
    </div>
  );
}
